import { useState, type CSSProperties } from 'react'
import { DatePicker, Popover, Toolbar } from '@cloudflare/kumo'
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react'
import { todayYmdInTz } from '../../lib/calendar-time'

/**
 * Local stand-in for react-day-picker's `Matcher` type (not a direct
 * dependency of this app — see the DayNavigator plan). Only the two shapes
 * DayNavigator needs (open-ended before/after bounds) are modeled.
 */
type DisabledMatcher = { before: Date } | { after: Date }

/**
 * Parse a `YYYY-MM-DD` string into a local `Date` anchored at noon, to avoid
 * UTC-drift when the value is later formatted back with local getters.
 * Returns `undefined` for empty/malformed input.
 */
function ymdToLocalDate(ymd: string): Date | undefined {
  const parts = ymd.split('-').map(Number)
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return undefined
  const [y, m, d] = parts as [number, number, number]
  return new Date(y, m - 1, d, 12, 0, 0, 0)
}

/** Format a `Date` back to `YYYY-MM-DD` using local getters (no UTC drift). */
function localDateToYmd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

/**
 * Reformat a `YYYY-MM-DD` string as `dd/MM/yyyy`, as a pure string operation
 * (no `Date`/`toLocaleDateString`) so the result is independent of the
 * browser's locale and immune to tz drift. Malformed input is returned as-is.
 */
function formatYmdAsDdMmYyyy(ymd: string): string {
  const parts = ymd.split('-')
  if (parts.length !== 3) return ymd
  const [y, m, d] = parts
  return `${d}/${m}/${y}`
}

export interface DayNavigatorProps {
  /** Selected date as `YYYY-MM-DD`. Empty string renders the placeholder. */
  value: string
  /** Fired when a day is picked in the calendar popover. */
  onChange: (ymd: string) => void
  /** Step back. The parent owns the step size (day/week/month). */
  onPrev: () => void
  /** Step forward. The parent owns the step size (day/week/month). */
  onNext: () => void
  /** Inclusive lower bound (`YYYY-MM-DD`) — dates before this are disabled. */
  min?: string
  /** Inclusive upper bound (`YYYY-MM-DD`) — dates after this are disabled. */
  max?: string
  /**
   * IANA timezone used to determine "today" for `todayLabel`. Defaults to the
   * browser's local timezone when omitted.
   */
  timezone?: string
  disabledPrev?: boolean
  disabledNext?: boolean
  prevLabel: string
  nextLabel: string
  openLabel: string
  /** Custom formatter for the trigger label. Defaults to `dd/MM/yyyy`. */
  dateLabel?: (ymd: string) => string
  /** Shown instead of the formatted date when the value is today, in `timezone`. */
  todayLabel?: string
  /** Shown on the trigger when `value` is empty. Defaults to `openLabel`. */
  placeholder?: string
  prevTestId?: string
  nextTestId?: string
  triggerTestId?: string
  size?: 'xs' | 'sm' | 'base' | 'lg'
  className?: string
}

const triggerStyle: CSSProperties = { minWidth: '9rem', justifyContent: 'center' }

/**
 * Shared date-navigation control: `[◀ prev] [date trigger → calendar popover] [▶ next]`.
 *
 * Controlled + callback-based, i18n-agnostic (labels are passed in as props).
 * Used by `BuildingCalendarPage` (view-mode-aware stepping) and
 * `BookingFormCore` (single-day stepping with a create-time `min` floor).
 */
export function DayNavigator({
  value,
  onChange,
  onPrev,
  onNext,
  min,
  max,
  timezone,
  disabledPrev,
  disabledNext,
  prevLabel,
  nextLabel,
  openLabel,
  dateLabel,
  todayLabel,
  placeholder,
  prevTestId,
  nextTestId,
  triggerTestId,
  size,
  className,
}: DayNavigatorProps) {
  const [open, setOpen] = useState(false)

  const selectedDate = ymdToLocalDate(value)
  const minDate = min ? ymdToLocalDate(min) : undefined
  const maxDate = max ? ymdToLocalDate(max) : undefined
  const disabledMatchers: DisabledMatcher[] = []
  if (minDate) disabledMatchers.push({ before: minDate })
  if (maxDate) disabledMatchers.push({ after: maxDate })

  const effectiveTimezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
  const label = value
    ? dateLabel
      ? dateLabel(value)
      : todayLabel && value === todayYmdInTz(effectiveTimezone)
        ? todayLabel
        : formatYmdAsDdMmYyyy(value)
    : (placeholder ?? openLabel)

  function handlePick(d?: Date) {
    if (d) {
      onChange(localDateToYmd(d))
      setOpen(false)
    }
  }

  return (
    <Toolbar size={size} className={className}>
      <Toolbar.Button
        icon={CaretLeftIcon}
        shape="square"
        aria-label={prevLabel}
        onClick={onPrev}
        disabled={disabledPrev}
        data-testid={prevTestId}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <Popover.Trigger
          render={
            <Toolbar.Button
              aria-label={openLabel}
              data-testid={triggerTestId}
              data-value={value}
              style={triggerStyle}
            />
          }
        >
          <span className="tabular-nums whitespace-nowrap">{label}</span>
        </Popover.Trigger>
        <Popover.Content>
          <DatePicker
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate}
            disabled={disabledMatchers.length > 0 ? disabledMatchers : undefined}
            onChange={handlePick}
          />
        </Popover.Content>
      </Popover>
      <Toolbar.Button
        icon={CaretRightIcon}
        shape="square"
        aria-label={nextLabel}
        onClick={onNext}
        disabled={disabledNext}
        data-testid={nextTestId}
      />
    </Toolbar>
  )
}
